import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { OG_IMAGE_URL } from '../pages/home-redesign/cityLinks';
import LegalPageShell from '../components/LegalPageShell';

type LegalSubsection = {
  heading?: string;
  paragraphs?: string[];
  items?: string[];
};

type LegalSection = LegalSubsection & {
  heading: string;
  subsections?: LegalSubsection[];
};

const Terms = () => {
  const { t, currentLanguage } = useLanguage();
  const sections = t('terms.sections') as unknown as LegalSection[];

  const renderText = (text: string, key: string) => {
    const parts = text.split(/(\{\{privacy\}\}|\{\{guidelines\}\}|\{\{safety\}\}|\{\{odr\}\}|\{\{legal\}\})/g);

    return parts.map((part, index) => {
      const partKey = `${key}-${index}`;

      if (part === '{{privacy}}') {
        return (
          <Link key={partKey} to="/privacy">
            {t('terms.links.privacy')}
          </Link>
        );
      }

      if (part === '{{guidelines}}') {
        return (
          <Link key={partKey} to="/guidelines">
            {t('terms.links.guidelines')}
          </Link>
        );
      }

      if (part === '{{safety}}') {
        return (
          <Link key={partKey} to="/safety">
            {t('terms.links.safety')}
          </Link>
        );
      }

      if (part === '{{odr}}') {
        return (
          <a
            key={partKey}
            href="https://ec.europa.eu/consumers/odr"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://ec.europa.eu/consumers/odr
          </a>
        );
      }

      if (part === '{{legal}}') {
        return (
          <a key={partKey} href="mailto:legal@chisme.social">
            legal@chisme.social
          </a>
        );
      }

      return part;
    });
  };

  const renderBlock = (block: LegalSubsection, keyPrefix: string) => (
    <>
      {block.paragraphs?.map((paragraph, index) => (
        <p key={`${keyPrefix}-p-${index}`}>{renderText(paragraph, `${keyPrefix}-p-${index}`)}</p>
      ))}
      {block.items && (
        <ul>
          {block.items.map((item, index) => (
            <li key={`${keyPrefix}-li-${index}`}>{renderText(item, `${keyPrefix}-li-${index}`)}</li>
          ))}
        </ul>
      )}
    </>
  );

  useSeoMeta({
    title: t('terms.seoTitle'),
    description: t('terms.seoDescription'),
    url: 'https://chisme.social/terms',
    image: OG_IMAGE_URL,
    locale: currentLanguage === 'en' ? 'en' : 'es',
  });

  return (
    <LegalPageShell
      backLabel={t('terms.backHome')}
      title={t('terms.title')}
      lastUpdatedLabel={t('terms.lastUpdated')}
      lastUpdatedDate={t('terms.lastUpdatedDate')}
    >
      {sections.map((section, sectionIndex) => (
        <section key={section.heading}>
          <h2>{section.heading}</h2>
          {renderBlock(section, `section-${sectionIndex}`)}
          {section.subsections?.map((subsection, subsectionIndex) => (
            <React.Fragment key={subsection.heading ?? `${section.heading}-${subsectionIndex}`}>
              {subsection.heading && <h3>{subsection.heading}</h3>}
              {renderBlock(subsection, `section-${sectionIndex}-subsection-${subsectionIndex}`)}
            </React.Fragment>
          ))}
        </section>
      ))}
    </LegalPageShell>
  );
};

export default Terms;
